import { type QueryClient, notifyManager } from "@tanstack/react-query";
import type { ModelName } from "../types";
import { buildTellescopeQueryKey } from "../keys";
import { TellescopeModelFromName } from "../mappings";

/**
 * Writes each item in `items` into the `getOne` cache slot for its model and
 * ID, so that subsequent `getOne` queries can be served from the cache without
 * a network round-trip.
 *
 * Typically called after a collection fetch (`getSome`, `getByIds`) to
 * pre-populate individual item caches as a side-effect.
 *
 * All cache writes are wrapped in `notifyManager.batch()` so that observer
 * notifications are coalesced into a single React render pass, avoiding a
 * cascade of re-renders when seeding many items at once.
 *
 * @param queryClient - The TanStack Query client whose cache will be written to.
 * @param model - The Tellescope model name (e.g. `"endusers"`).
 * @param items - The collection of model items to seed into the cache.
 */
export const seedIndividualQueryCache = <Model extends ModelName>({
  queryClient,
  model,
  items,
}: {
  queryClient: QueryClient;
  model: Model;
  items: ReadonlyArray<TellescopeModelFromName<Model>>;
}) => {
  notifyManager.batch(() => {
    items.forEach((item) => {
      const queryKey = buildTellescopeQueryKey<Model, "getOne">({
        model,
        method: "getOne",
        // @ts-expect-error every model has an id, but TS doesn't know that
        request: { id: item.id },
      });

      queryClient.setQueryData(queryKey, item);
    });
  });
};
